import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selfAllocationApi } from "../api/selfAllocation";
import type {
  SelfAllocationCreatePayload,
  SelfAllocationUpdatePayload,
} from "../types/selfAllocation";

const QK = "self-allocation";

export function useSelfAllocations(
  params?: Record<string, string | number | undefined>,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [QK, "list", params],
    queryFn: () => selfAllocationApi.list(params),
    enabled,
    staleTime: 0,
  });
}

export function useSelfAllocation(id: string | undefined) {
  return useQuery({
    queryKey: [QK, "detail", id],
    queryFn: () => selfAllocationApi.get(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useSelfAllocationEligibility(enabled: boolean = true) {
  return useQuery({
    queryKey: [QK, "eligibility"],
    queryFn: () => selfAllocationApi.eligibility(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSelfAllocationAvailableProjects(
  id: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [QK, "available-projects", id],
    queryFn: () => selfAllocationApi.availableProjects(id!),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function useSelfAllocationAvailableProjectGroups(
  id: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [QK, "available-project-groups", id],
    queryFn: () => selfAllocationApi.availableProjectGroups(id!),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function useCreateSelfAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SelfAllocationCreatePayload) => selfAllocationApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useUpdateSelfAllocation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SelfAllocationUpdatePayload) => selfAllocationApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useDeleteSelfAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => selfAllocationApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useSubmitSelfAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => selfAllocationApi.submit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useApproveSelfAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => selfAllocationApi.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useRejectSelfAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejection_reason }: { id: string; rejection_reason: string }) =>
      selfAllocationApi.reject(id, rejection_reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useSelfAllocationMissing(
  year: number | undefined,
  month: number | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [QK, "missing", year, month],
    queryFn: () => selfAllocationApi.missing(year!, month!),
    enabled: enabled && !!year && !!month,
    staleTime: 0,
  });
}

export function useSelfAllocationActionCount(enabled: boolean) {
  return useQuery({
    queryKey: [QK, "action-count"],
    queryFn: () => selfAllocationApi.actionCount(),
    enabled,
    refetchInterval: 60_000,
  });
}
